import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import koKR from 'antd/locale/ko_KR';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import NoticeManagement from './pages/NoticeManagement';
import OrganizationManagement from './pages/OrganizationManagement';
import MenuManagement from './pages/MenuManagement';
import MenuPermissions from './pages/MenuPermissions';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import MainLayout from './layouts/MainLayout';
import { supabase } from './services/supabase';
import 'antd/dist/reset.css';
import './App.css';
import { logger } from './utils/logger';
import { storageHelper } from './utils/storageHelper';

// 인증 체크 컴포넌트
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: userData } = await supabase
          .from('users')
          .select('has_admin_role, is_admin, is_active')
          .eq('auth_id', session.user.id)
          .single();

        if (userData && (userData.has_admin_role || userData.is_admin) && userData.is_active) {
          setIsAuthenticated(true);
        }
      }
    } catch (error) {
      logger.error('Auth check error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>로딩...</div>;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

function App() {
  useEffect(() => {
    // 세션 변경 감지
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        storageHelper.clearAll();
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  return (
    <ConfigProvider locale={koKR}>
      <AntdApp>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <MainLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="notices" element={<NoticeManagement />} />
              <Route path="organizations" element={<OrganizationManagement />} />
              <Route path="menus" element={<MenuManagement />} />
              <Route path="menu-permissions" element={<MenuPermissions />} />
              <Route path="settings" element={<Settings />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
